import { Location } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';

import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { AlertInfo } from '../../../../models/alert-info';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { QualityManagementService } from '../../../../services/quality-management.service';
import { FormService } from '../../../../services/form.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';
import { RiskTimeFramePayload } from '../../../../models/entities/risk';

@Component({
  selector: 'risk-time-frame-form',
  imports: [InputFieldComponent, ReactiveFormsModule, NgbAlert],
  templateUrl: './risk-time-frame-form.component.html',
  styleUrl: './risk-time-frame-form.component.scss'
})
export class RiskTimeFrameFormComponent implements OnInit {

  @Input() riskTimeFrame?: any;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  form!: FormGroup;
  canModify: boolean = false;
  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  constructor(
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private qualityManagementService: QualityManagementService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private location: Location
  ) { }

  ngOnInit(): void {
    this.form = new FormGroup({
      code: new FormControl('', Validators.required),
      name: new FormControl('', Validators.required)
    });

    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      this.canModify = this.accessControlService.hasFeatureActionAccess(
        profileCode,
        'QUALITY_MANAGEMENT_SETTINGS_RISK_TIME_FRAMES',
        'modify'
      );

      if (this.canModify) {
        this.form.enable({ emitEvent: false });
      } else {
        this.form.disable({ emitEvent: false });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['riskTimeFrame'] && changes['riskTimeFrame'].currentValue) {
      this.form?.patchValue({
        code: this.riskTimeFrame.code,
        name: this.riskTimeFrame.name
      });
    }
  }

  getCodeInput(): FormControl { return this.getFormControl('code'); }
  getNameInput(): FormControl { return this.getFormControl('name'); }
  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }

  submitForm(): void {
    if (!this.canModify) {
      return;
    }

    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    if (!this.form.valid) {
      return;
    }

    this.alertInfo.show = false;
    this.formService.resetErrors(this.form);

    const values: RiskTimeFramePayload = this.form.getRawValue();
    const riskTimeFrameId = this.riskTimeFrame?.riskTimeFrameId;

    const request$ = riskTimeFrameId
      ? this.qualityManagementService.updateRiskTimeFrame(Number(riskTimeFrameId), values)
      : this.qualityManagementService.createRiskTimeFrame(values);

    request$.subscribe({
      next: (response: any) => this.submitFormSuccess(response),
      error: (error: any) => this.submitFormError(error)
    });
  }

  goBack(): void {
    this.location.back();
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => this.alertInfo.show = false, 3000);
  }

  private submitFormSuccess(response: any): void {
    this.alertNotificationService.success('Operation completed successfully');
    this.showAlertInfo('success', 'Operation completed successfully');

    this.onSavedData.emit({ success: true, response });
  }

  private submitFormError(error: any): void {
    const apiError = error?.error?.error;

    if (apiError?.description === 'ARGUMENT_VALIDATION_ERROR') {
      this.formService.resetErrors(this.form);
      this.formService.setValidationErrors(this.form, apiError.validationErrors);
    } else {
      this.showAlertInfo(
        'danger',
        this.qualityManagementService.riskErrorMessage(
          apiError,
          apiError?.message ?? 'Failed to save the risk time frame.'
        )
      );
    }

    this.onSavedData.emit({ success: false, error });
  }
}
